import { Injectable, computed, signal } from "@angular/core";
import type {
  ContentBlockData,
  ContentBlockKind,
  ResolvedToolCall,
  BackgroundJobBlockData,
  BackgroundJobSnapshot,
} from "../model/block.types";
import type { InterruptPayload } from "../model/interrupt.types";
import type { PlanData, PlanStep } from "../model/plan.types";
import type { ReasoningStepInfo } from "./reasoning-step.service";

export interface MockItem<T> {
  id: string;
  label: string;
  enabled: ReturnType<typeof signal<boolean>>;
  data: T;
}

/** A `ContentBlockCardComponent` mock — `kind` travels separately from `data` (envelope-level, post-Fase 3). */
export interface ContentBlockMockItem {
  id: string;
  label: string;
  enabled: ReturnType<typeof signal<boolean>>;
  kind: ContentBlockKind;
  data: ContentBlockData;
}

export interface BgJobMockItem {
  id: string;
  label: string;
  enabled: ReturnType<typeof signal<boolean>>;
  snapshot: BackgroundJobSnapshot;
}

export interface ToolCallMockItem {
  id: string;
  label: string;
  enabled: ReturnType<typeof signal<boolean>>;
  data: ResolvedToolCall;
}

/**
 * A plan mock — `view: "card"` renders via `PlanCardComponent`, `view: "progress"` via
 * `PlanProgressCardComponent` (the durable `plan` input fallback, no stream mock needed).
 */
export interface PlanMockItem {
  id: string;
  label: string;
  enabled: ReturnType<typeof signal<boolean>>;
  view: "card" | "progress";
  plan: PlanData;
}

/**
 * Evidence-progress toggle mock — the full `evidence_progress` AG-UI Custom envelope sequence
 * replayed into the panel-local `EvidenceProgressStore` when the entry is enabled.
 */
export interface EvidenceMockItem {
  id: string;
  label: string;
  enabled: ReturnType<typeof signal<boolean>>;
  events: Record<string, unknown>[];
}

// ── GeoJSON ────────────────────────────────────────────────────────────────────

const MOCK_GEOJSON = {
  type: "FeatureCollection",
  features: [
    {
      type: "Feature",
      geometry: {
        type: "Polygon",
        coordinates: [
          [
            [-80.19, 25.77],
            [-80.18, 25.77],
            [-80.18, 25.78],
            [-80.19, 25.78],
            [-80.19, 25.77],
          ],
        ],
      },
      properties: {
        parcel_id: "01-4138-014-1690",
        address: "123 NW 7th Ave, Miami FL",
        area_sqft: 7200,
        year_built: 1962,
        stories: 2,
      },
    },
    {
      type: "Feature",
      geometry: {
        type: "Polygon",
        coordinates: [
          [
            [-80.2, 25.76],
            [-80.19, 25.76],
            [-80.19, 25.77],
            [-80.2, 25.77],
            [-80.2, 25.76],
          ],
        ],
      },
      properties: {
        parcel_id: "01-4138-014-1700",
        address: "456 NW 8th Ave, Miami FL",
        area_sqft: 9400,
        year_built: 1978,
        stories: 1,
      },
    },
  ],
};

// ── Tool card mock data ────────────────────────────────────────────────────────

const TOOL_MOCKS: ContentBlockMockItem[] = [
  {
    id: "tool_geojson",
    label: "GeoJSON — property_metadata_extract (2 parcels, Miami)",
    enabled: signal(false),
    kind: "geometry",
    data: {
      format: "geojson",
      label: "property_metadata_extract",
      ref: null,
      value: MOCK_GEOJSON,
      metadata: { bbox: [-80.2, 25.76, -80.18, 25.78], center: [-80.19, 25.77], crs: "EPSG:4326", feature_count: 2 },
      source: {},
    },
  },
  {
    id: "tool_image",
    label: "Image — ortho_extract (vertical view)",
    enabled: signal(false),
    kind: "image",
    data: {
      format: "image",
      label: "ortho_extract",
      ref: null,
      value: "mock://files/ortho_thumb.jpg",
      metadata: { resolution_x: 4096, resolution_y: 4096, bands: 3 },
      source: {},
    },
  },
  {
    id: "tool_text",
    label: "Text — ortho_imagery_collections (status metadata)",
    enabled: signal(false),
    kind: "blob",
    data: {
      format: "text",
      label: "ortho_imagery_collections",
      ref: null,
      value:
        "Collection: Miami_2024_Q2\nCapture date: 2024-05-20\nGSD: 3.0 cm\nSensor: ULTRACAM OSPREY 4.1\nCloud cover: 2%\nOverlap: 80%\nSRS: EPSG:4326\nStatus: AVAILABLE",
      metadata: { page_count: null, title: "Collection status", char_count: 180 },
      source: {},
    },
  },
  {
    id: "tool_json",
    label: "JSON — property_report (single parcel)",
    enabled: signal(false),
    kind: "structured",
    data: {
      format: "json",
      label: "property_report",
      ref: null,
      value: {
        parcel_id: "01-4138-014-1690",
        address: "123 NW 7th Ave, Miami FL",
        area_sqft: 7200,
        year_built: 1962,
        stories: 2,
        roof_type: "flat",
        zoning: "T6-8-O",
        market_value: 485000,
        damage_score: null,
      },
      metadata: { key_count: 9, top_level_keys: ["parcel_id", "address", "area_sqft", "year_built", "stories"] },
      source: {},
    },
  },
  {
    id: "tool_binary",
    label: "Binary — bundle_generate (ZIP export)",
    enabled: signal(false),
    kind: "blob",
    data: {
      format: "binary",
      label: "bundle_generate",
      ref: null,
      value: "mock://export_miami_20240520.zip",
      metadata: { page_count: null, title: "ZIP export", char_count: null },
      source: {},
    },
  },
  {
    id: "tool_file_geojson",
    label: "FileBlock — GeoJSON (property_metadata_extract, file store)",
    enabled: signal(false),
    kind: "geometry",
    data: {
      format: "geojson",
      label: "property_metadata_extract",
      ref: {
        file_id: "file-properties-001",
        url: "mock://files/properties_miami.geojson",
        mime_type: "application/geo+json",
        size_bytes: 44_800,
        filename: "properties_miami.geojson",
        preview_url: null,
      },
      value: null,
      metadata: { bbox: [-80.2, 25.76, -80.18, 25.78], center: [-80.19, 25.77], crs: "EPSG:4326", feature_count: 2 },
      source: {},
    },
  },
  {
    id: "tool_file_image",
    label: "FileBlock — Image (ortho_extract, file store with preview)",
    enabled: signal(false),
    kind: "image",
    data: {
      format: "image",
      label: "ortho_extract",
      ref: {
        file_id: "file-ortho-001",
        url: "mock://files/ortho_20240520.jpg",
        mime_type: "image/jpeg",
        size_bytes: 2_500_000,
        filename: "ortho_20240520.jpg",
        preview_url: "mock://files/ortho_20240520_thumb.jpg",
      },
      value: null,
      metadata: { resolution_x: 4096, resolution_y: 4096, bands: 3 },
      source: {},
    },
  },
];

// ── Background job mock data ───────────────────────────────────────────────────

const BG_JOB_BLOCK_BASE: BackgroundJobBlockData = {
  job_id: "mock-bg",
  job_thread_id: "thread-bg-mock",
  job_run_id: "run-bg-mock",
  assistant_id: "pipeline_background",
  task_type: "pipeline",
  display_name: "Pipeline: Analyze Miami Properties",
  expected_phases: ["discover", "enrich", "deep_analysis", "report"],
  expected_phase_labels: {
    discover: "Discovering features",
    enrich: "Enriching data",
    deep_analysis: "Running deep analysis",
    report: "Generating report",
  },
  poll_interval_ms: 2000,
};

const now = Date.now();

export const BG_JOB_RUNNING_SNAPSHOT: BackgroundJobSnapshot = {
  block: { ...BG_JOB_BLOCK_BASE, job_id: "mock-bg-running" },
  progress: {
    status: "running",
    current_phase: "deep_analysis",
    phases_total: 4,
    phases_completed: 2,
    started_at: new Date(now - 65_000).toISOString(),
    completed_at: null,
    phases: [
      {
        name: "discover",
        label: "Discovering features",
        status: "completed",
        started_at: new Date(now - 65_000).toISOString(),
        completed_at: new Date(now - 55_000).toISOString(),
        record_count: 142,
        error_message: null,
        steps: [],
      },
      {
        name: "enrich",
        label: "Enriching data",
        status: "completed",
        started_at: new Date(now - 55_000).toISOString(),
        completed_at: new Date(now - 40_000).toISOString(),
        record_count: null,
        error_message: null,
        steps: [],
      },
      {
        name: "deep_analysis",
        label: "Running deep analysis",
        status: "running",
        started_at: new Date(now - 40_000).toISOString(),
        completed_at: null,
        record_count: null,
        error_message: null,
        steps: [],
      },
      {
        name: "report",
        label: "Generating report",
        status: "pending",
        started_at: null,
        completed_at: null,
        record_count: null,
        error_message: null,
        steps: [],
      },
    ],
  },
  output_refs: [],
  run_status: "running",
  run_error_message: null,
  subAgentTodos: [],
  subAgentLabel: null,
  phaseStates: {},
  phaseSteps: {},
  deepAnalysis: { toolCall: null, message: "" },
};

export const BG_JOB_SUCCESS_SNAPSHOT: BackgroundJobSnapshot = {
  block: { ...BG_JOB_BLOCK_BASE, job_id: "mock-bg-success" },
  progress: {
    status: "completed",
    current_phase: null,
    phases_total: 4,
    phases_completed: 4,
    started_at: new Date(now - 105_000).toISOString(),
    completed_at: new Date(now - 30_000).toISOString(),
    phases: [
      {
        name: "discover",
        label: "Discovering features",
        status: "completed",
        started_at: new Date(now - 105_000).toISOString(),
        completed_at: new Date(now - 90_000).toISOString(),
        record_count: 142,
        error_message: null,
        steps: [],
      },
      {
        name: "enrich",
        label: "Enriching data",
        status: "completed",
        started_at: new Date(now - 90_000).toISOString(),
        completed_at: new Date(now - 65_000).toISOString(),
        record_count: null,
        error_message: null,
        steps: [],
      },
      {
        name: "deep_analysis",
        label: "Running deep analysis",
        status: "completed",
        started_at: new Date(now - 65_000).toISOString(),
        completed_at: new Date(now - 40_000).toISOString(),
        record_count: null,
        error_message: null,
        steps: [],
      },
      {
        name: "report",
        label: "Generating report",
        status: "completed",
        started_at: new Date(now - 40_000).toISOString(),
        completed_at: new Date(now - 30_000).toISOString(),
        record_count: null,
        error_message: null,
        steps: [],
      },
    ],
  },

  output_refs: [
    {
      ref_id: "ref-csv-001",
      filename: "miami_properties_analysis.csv",
      url: "mock://output/csv",
      content_type: "text/csv",
      content_category: "csv",
    },
    {
      ref_id: "ref-geo-001",
      filename: "miami_properties_analysis.geojson",
      url: "mock://output/geojson",
      content_type: "application/geo+json",
      content_category: "geojson",
    },
  ],
  run_status: "success",
  run_error_message: null,
  subAgentTodos: [],
  subAgentLabel: null,
  phaseStates: {},
  phaseSteps: {},
  deepAnalysis: { toolCall: null, message: "" },
};

export const BG_JOB_ERROR_SNAPSHOT: BackgroundJobSnapshot = {
  block: { ...BG_JOB_BLOCK_BASE, job_id: "mock-bg-error" },
  progress: {
    status: "error",
    current_phase: "deep_analysis",
    phases_total: 4,
    phases_completed: 2,
    started_at: new Date(now - 80_000).toISOString(),
    completed_at: null,
    phases: [
      {
        name: "discover",
        label: "Discovering features",
        status: "completed",
        started_at: new Date(now - 80_000).toISOString(),
        completed_at: new Date(now - 70_000).toISOString(),
        record_count: 88,
        error_message: null,
        steps: [],
      },
      {
        name: "enrich",
        label: "Enriching data",
        status: "completed",
        started_at: new Date(now - 70_000).toISOString(),
        completed_at: new Date(now - 55_000).toISOString(),
        record_count: null,
        error_message: null,
        steps: [],
      },
      {
        name: "deep_analysis",
        label: "Running deep analysis",
        status: "error",
        started_at: new Date(now - 55_000).toISOString(),
        completed_at: null,
        record_count: null,
        error_message: "Deep analysis agent failed: insufficient coverage data for damage scoring",
        steps: [],
      },
      {
        name: "report",
        label: "Generating report",
        status: "pending",
        started_at: null,
        completed_at: null,
        record_count: null,
        error_message: null,
        steps: [],
      },
    ],
  },
  output_refs: [],
  run_status: "error",
  run_error_message: null,
  subAgentTodos: [],
  subAgentLabel: null,
  phaseStates: {},
  phaseSteps: {},
  deepAnalysis: { toolCall: null, message: "" },
};

const BG_JOB_MOCKS: BgJobMockItem[] = [
  {
    id: "bg_running",
    label: "Background job — Running (2/4 phases, 142 properties)",
    enabled: signal(false),
    snapshot: BG_JOB_RUNNING_SNAPSHOT,
  },
  {
    id: "bg_success",
    label: "Background job — Success (CSV + GeoJSON outputs, Load in map)",
    enabled: signal(false),
    snapshot: BG_JOB_SUCCESS_SNAPSHOT,
  },
  {
    id: "bg_error",
    label: "Background job — Phase error (deep analysis failed)",
    enabled: signal(false),
    snapshot: BG_JOB_ERROR_SNAPSHOT,
  },
];

// ── HITL mock data ─────────────────────────────────────────────────────────────

const HITL_MOCKS: MockItem<InterruptPayload>[] = [
  {
    id: "hitl_confirm_pipeline",
    label: "HITL — Pipeline billing gate (is_confirmation)",
    enabled: signal(false),
    data: {
      type: "clarification",
      display_text:
        "The pipeline agent wants to launch a property analysis job. This will consume API credits based on the number of properties discovered.",
      items: [
        {
          id: "task-001__pipeline__task-001",
          task_id: "task-001",
          subagent_type: "pipeline",
          key: "pipeline__task-001",
          kind: "boolean",
          display_text:
            "Launch pipeline: Pipeline: Analyze Miami Properties\n\nPhases: Discovering features, Enriching data, Running analysis, Generating report",
          placeholder: "",
          options: [],
          is_confirmation: true,
          tool_name: "",
          tool_args: {},
        },
      ],
      round: 1,
    },
  },
  {
    id: "hitl_confirm",
    label: "HITL — Confirm dialog (generic boolean)",
    enabled: signal(false),
    data: {
      type: "clarification",
      display_text:
        "The agent is about to run a batch ortho extraction on 12 parcels. This will consume approximately 120 API credits.",
      items: [
        {
          id: "task-002__confirm",
          task_id: "task-002",
          subagent_type: "extraction_agent",
          key: "confirm",
          kind: "boolean",
          display_text: "Do you want to proceed with the batch extraction?",
          placeholder: "",
          options: [],
          is_confirmation: true,
          tool_name: "",
          tool_args: {},
        },
      ],
      round: 1,
    },
  },
  {
    id: "hitl_select",
    label: "HITL — Single select (image_type)",
    enabled: signal(false),
    data: {
      type: "clarification",
      display_text: "The agent needs one more detail before starting the extraction.",
      items: [
        {
          id: "task-003__image_type",
          task_id: "task-003",
          subagent_type: "imagery_agent",
          key: "image_type",
          kind: "select",
          display_text: "Which image type would you like to use for the ortho extraction?",
          placeholder: "",
          options: [
            { id: "vertical", label: "Vertical (nadir view)" },
            { id: "front", label: "Front oblique" },
            { id: "back", label: "Back oblique" },
            { id: "left", label: "Left oblique" },
          ],
          is_confirmation: false,
          tool_name: "",
          tool_args: {},
        },
      ],
      round: 1,
    },
  },
  {
    id: "hitl_multiselect",
    label: "HITL — Multi-select (export layers)",
    enabled: signal(false),
    data: {
      type: "clarification",
      display_text: "Choose which data layers to include in the bundle export.",
      items: [
        {
          id: "task-004__layers",
          task_id: "task-004",
          subagent_type: "export_agent",
          key: "layers",
          kind: "multiselect",
          display_text: "Select the data layers you want to include in the export:",
          placeholder: "",
          options: [
            { id: "ortho", label: "Orthophoto (RGB)" },
            { id: "dsm", label: "Digital Surface Model (DSM)" },
            { id: "dtm", label: "Digital Terrain Model (DTM)" },
            { id: "damage", label: "Damage Assessment Layer" },
          ],
          is_confirmation: false,
          tool_name: "",
          tool_args: {},
        },
      ],
      round: 1,
    },
  },
  {
    id: "hitl_date",
    label: "HITL — Date field (capture date range)",
    enabled: signal(false),
    data: {
      type: "clarification",
      display_text: "Please specify the capture date range for the imagery search.",
      items: [
        {
          id: "task-005__start_date",
          task_id: "task-005",
          subagent_type: "search_agent",
          key: "start_date",
          kind: "date",
          display_text: "Start date",
          placeholder: "2024-01-01",
          options: [],
          is_confirmation: false,
          tool_name: "",
          tool_args: {},
        },
      ],
      round: 1,
    },
  },
  {
    id: "hitl_text",
    label: "HITL — Text area (analysis description)",
    enabled: signal(false),
    data: {
      type: "clarification",
      display_text: "Help the agent understand the scope of the analysis.",
      items: [
        {
          id: "task-006__description",
          task_id: "task-006",
          subagent_type: "analysis_agent",
          key: "description",
          kind: "text",
          display_text: "Describe the area of interest and any specific features to include",
          placeholder: "Downtown Miami, focus on commercial flat-roof buildings built before 1980…",
          options: [],
          is_confirmation: false,
          tool_name: "",
          tool_args: {},
        },
      ],
      round: 1,
    },
  },
  {
    id: "hitl_geocoding",
    label: "HITL — Geocoding (location search)",
    enabled: signal(false),
    data: {
      type: "clarification",
      display_text: "The agent needs a location to start the property search.",
      items: [
        {
          id: "task-007__location",
          task_id: "task-007",
          subagent_type: "search_agent",
          key: "location",
          kind: "geocoding",
          display_text: "Area of interest",
          placeholder: "Miami, Florida",
          options: [],
          is_confirmation: false,
          tool_name: "",
          tool_args: {},
        },
      ],
      round: 1,
    },
  },
  {
    id: "hitl_geometry",
    label: "HITL — Geometry (spatial AOI input)",
    enabled: signal(false),
    data: {
      type: "clarification",
      display_text: "The agent needs a spatial boundary for the pipeline analysis.",
      items: [
        {
          id: "task-008__aoi_geom",
          task_id: "task-008",
          subagent_type: "analysis_agent",
          key: "aoi_geom",
          kind: "geometry",
          display_text: "Area of interest geometry",
          placeholder: "POLYGON((-80.22 25.76, -80.22 25.79, -80.18 25.79, -80.18 25.76, -80.22 25.76))",
          options: [],
          is_confirmation: false,
          tool_name: "",
          tool_args: {},
        },
      ],
      round: 1,
    },
  },
  {
    id: "hitl_multi_entry",
    label: "HITL — Múltiples entradas (geocoding + select + multiselect)",
    enabled: signal(false),
    data: {
      type: "clarification",
      display_text: "The agent needs a few details before starting the extraction.",
      items: [
        {
          id: "task-009__location",
          task_id: "task-009",
          subagent_type: "search_agent",
          key: "location",
          kind: "geocoding",
          display_text: "Area of interest",
          placeholder: "Miami, Florida",
          options: [],
          is_confirmation: false,
          tool_name: "",
          tool_args: {},
        },
        {
          id: "task-009__image_type",
          task_id: "task-009",
          subagent_type: "search_agent",
          key: "image_type",
          kind: "select",
          display_text: "Image type",
          placeholder: "",
          options: [
            { id: "vertical", label: "Vertical (nadir view)" },
            { id: "front", label: "Front oblique" },
            { id: "back", label: "Back oblique" },
          ],
          is_confirmation: false,
          tool_name: "",
          tool_args: {},
        },
        {
          id: "task-009__layers",
          task_id: "task-009",
          subagent_type: "search_agent",
          key: "layers",
          kind: "multiselect",
          display_text: "Data layers to include",
          placeholder: "",
          options: [
            { id: "ortho", label: "Orthophoto (RGB)" },
            { id: "dsm", label: "Digital Surface Model (DSM)" },
            { id: "dtm", label: "Digital Terrain Model (DTM)" },
          ],
          is_confirmation: false,
          tool_name: "",
          tool_args: {},
        },
      ],
      round: 1,
    },
  },
  {
    id: "hitl_frontend_tools",
    label: "HITL — Frontend tools (move_to Miami)",
    enabled: signal(false),
    data: {
      type: "frontend_tools",
      display_text: "Moving the map to Miami…",
      items: [
        {
          id: "task-010__move_to",
          task_id: "task-010",
          subagent_type: "map_agent",
          key: "move_to",
          kind: "tool_call",
          display_text: "",
          placeholder: "",
          options: [],
          is_confirmation: false,
          tool_name: "move_to",
          tool_args: { latitude: 25.7617, longitude: -80.1918 },
        },
      ],
      round: 1,
    },
  },
  {
    id: "hitl_number",
    label: "HITL — Number field (buffer distance)",
    enabled: signal(false),
    data: {
      type: "clarification",
      display_text: "The agent needs a buffer distance before running the spatial filter.",
      items: [
        {
          id: "task-011__buffer_distance",
          task_id: "task-011",
          subagent_type: "analysis_agent",
          key: "buffer_distance",
          kind: "number",
          display_text: "Buffer distance (m)",
          placeholder: "30",
          options: [],
          is_confirmation: false,
          tool_name: "",
          tool_args: {},
        },
      ],
      round: 1,
    },
  },
  {
    id: "hitl_select_ambiguous",
    label: "HITL — Ambiguous select (__none__ last + binding detail)",
    enabled: signal(false),
    data: {
      type: "clarification",
      display_text: "Two evidence candidates match this task — pick the one to use.",
      items: [
        {
          id: "task-012__ortho_pick",
          task_id: "task-012",
          subagent_type: "extraction_agent",
          key: "ortho_pick",
          kind: "select",
          display_text: "Which ortho capture should feed the damage assessment?",
          placeholder: "",
          options: [
            { id: "ev-ortho-2024", label: "Ortho capture 2024-Q2 (3.0 cm GSD)" },
            { id: "ev-ortho-2023", label: "Ortho capture 2023-Q4 (7.5 cm GSD)" },
            { id: "__none__", label: "None of these" },
          ],
          is_confirmation: false,
          tool_name: "",
          tool_args: {},
          metadata: {
            issue_codes: ["AMBIGUOUS_EVIDENCE"],
            binding_detail: {
              candidates: [
                {
                  role: "ortho_input",
                  evidence_id: "ev-ortho-2024",
                  label: "Ortho capture 2024-Q2 (3.0 cm GSD)",
                  confidence: 0.72,
                  reasoning: "Newer capture with the requested GSD; roof edges align with the workarea.",
                },
                {
                  role: "ortho_input",
                  evidence_id: "ev-ortho-2023",
                  label: "Ortho capture 2023-Q4 (7.5 cm GSD)",
                  confidence: 0.41,
                  reasoning: "Older capture; too coarse for reliable damage scoring.",
                },
              ],
            },
          },
        },
      ],
      round: 1,
    },
  },
  {
    id: "hitl_reask_surfaces",
    label: "HITL — Re-asked text (plan_surfaces 2, cancel affordance)",
    enabled: signal(false),
    data: {
      type: "clarification",
      display_text: "The agent needs a clearer scope before retrying the analysis.",
      items: [
        {
          id: "task-013__aoi_notes",
          task_id: "task-013",
          subagent_type: "analysis_agent",
          key: "aoi_notes",
          kind: "text",
          display_text: "Describe what to include in the damage assessment (asked again — the previous answer was incomplete)",
          placeholder: "Focus on flat roofs with visible water damage…",
          options: [],
          is_confirmation: false,
          tool_name: "",
          tool_args: {},
          metadata: { plan_surfaces: 2 },
        },
      ],
      round: 2,
    },
  },
];

// ── Reasoning step mock data ───────────────────────────────────────────────────

const REASONING_MOCKS: MockItem<ReasoningStepInfo>[] = [
  {
    id: "reasoning_searching",
    label: "Reasoning — Searching imagery (completed)",
    enabled: signal(false),
    data: {
      stepLabel: "Searching imagery collections",
      description: "Looking for available ortho captures in the selected area.",
    },
  },
  {
    id: "reasoning_analyzing",
    label: "Reasoning — Analyzing properties (completed)",
    enabled: signal(false),
    data: {
      stepLabel: "Analyzing 142 properties",
      description: "Running structural damage assessment on discovered parcels.",
    },
  },
  {
    id: "reasoning_pending",
    label: "Reasoning — Analyzing… (streaming / pending)",
    enabled: signal(false),
    data: { stepLabel: "Analyzing…", description: "" },
  },
];

// ── Map layer card mock data (kind: "map_layer", rendered via MapLayerCardComponent) ──

const MAP_LAYER_STYLE_FLAT = {
  type: "flat",
  properties: { "fill-color": "#4575b4", "stroke-width": 1 },
};

const MAP_LAYER_STYLE_RULES = {
  type: "rules",
  rules: [
    { filter: [">", ["get", "area_sqft"], 8000], style: { "fill-color": "#d73027" } },
    { else: true, style: { "fill-color": "#4575b4" } },
  ],
};

const MAP_LAYER_MOCKS: ContentBlockMockItem[] = [
  {
    id: "map_layer_update_styled",
    label: "Map layer — update styled (layer-1, ol_styler flat)",
    enabled: signal(false),
    kind: "map_layer",
    data: {
      format: "geojson",
      label: "map_layer_layer-1",
      ref: {
        file_id: "file-map-layer-1",
        url: "mock://files/map_layer_layer-1.geojson",
        mime_type: "application/geo+json",
        size_bytes: 44_800,
        filename: "catastro_parcels.geojson",
        preview_url: null,
      },
      value: null,
      metadata: {
        bbox: [-80.2, 25.76, -80.18, 25.78],
        center: [-80.19, 25.77],
        zoom_hint: 15,
        crs: "EPSG:4326",
        feature_count: 2,
        content_summary: "2 cadastral parcels with roof attributes",
      },
      source: { evidence_id: "ev-map-layer-1", tool_name: "blackboard" },
      map_layer: {
        layer_id: "layer-1",
        name: "Catastro parcels",
        style: MAP_LAYER_STYLE_FLAT,
        style_metadata: { generated_by: "ol_styler" },
      },
    },
  },
  {
    id: "map_layer_add_unstyled",
    label: "Map layer — add unstyled (no layer id, no name, no style)",
    enabled: signal(false),
    kind: "map_layer",
    data: {
      format: "geojson",
      label: "map_layer_ev-map-layer-2",
      ref: {
        file_id: "file-map-layer-2",
        url: "mock://files/map_layer_nueva_zona.geojson",
        mime_type: "application/geo+json",
        size_bytes: 12_400,
        filename: "nueva_zona.geojson",
        preview_url: null,
      },
      value: null,
      metadata: { bbox: [-80.21, 25.75, -80.17, 25.79], crs: "EPSG:4326", feature_count: 1, content_summary: "1 drawn zone" },
      source: { evidence_id: "ev-map-layer-2", tool_name: "blackboard" },
      map_layer: { layer_id: null, name: null, style: null, style_metadata: null },
    },
  },
  {
    id: "map_layer_add_rules_styled",
    label: "Map layer — add styled (rules by area, no layer id)",
    enabled: signal(false),
    kind: "map_layer",
    data: {
      format: "geojson",
      label: "map_layer_ev-map-layer-3",
      ref: {
        file_id: "file-map-layer-3",
        url: "mock://files/map_layer_damage_zones.geojson",
        mime_type: "application/geo+json",
        size_bytes: 21_600,
        filename: "damage_zones.geojson",
        preview_url: null,
      },
      value: null,
      metadata: { bbox: [-80.22, 25.74, -80.16, 25.8], crs: "EPSG:4326", feature_count: 5, content_summary: "5 damage assessment zones" },
      source: { evidence_id: "ev-map-layer-3", tool_name: "blackboard" },
      map_layer: {
        layer_id: null,
        name: "Damage assessment zones",
        style: MAP_LAYER_STYLE_RULES,
        style_metadata: { generated_by: "ol_styler" },
      },
    },
  },
  {
    id: "map_layer_update_unstyled",
    label: "Map layer — update unstyled (layer-2, name from filename)",
    enabled: signal(false),
    kind: "map_layer",
    data: {
      format: "geojson",
      label: "map_layer_layer-2",
      ref: {
        file_id: "file-map-layer-4",
        url: "mock://files/map_layer_layer-2.geojson",
        mime_type: "application/geo+json",
        size_bytes: 9_800,
        filename: "inspeccion_barrios.geojson",
        preview_url: null,
      },
      value: null,
      metadata: { bbox: [-80.19, 25.75, -80.17, 25.79], crs: "EPSG:4326", feature_count: 3, content_summary: "3 neighbourhood inspections" },
      source: { evidence_id: "ev-map-layer-4", tool_name: "blackboard" },
      map_layer: { layer_id: "layer-2", name: null, style: null, style_metadata: null },
    },
  },
];

// ── Tool call hint mock data ───────────────────────────────────────────────────

const TOOL_CALL_MOCKS: ToolCallMockItem[] = [
  {
    id: "tool_call_pending",
    label: "Tool call hint — pending (property_metadata_extract)",
    enabled: signal(false),
    data: { id: "call-001", name: "property_metadata_extract", hasResult: false },
  },
  {
    id: "tool_call_done",
    label: "Tool call hint — done (ortho_extract)",
    enabled: signal(false),
    data: { id: "call-002", name: "ortho_extract", hasResult: true },
  },
];

// ── Plan card mock data (kind: "plan", rendered via PlanCard/PlanProgressCard) ─────────

/** Shared 2-step × 2-task decomposition of the plan mocks (realistic Miami roof-damage run). */
const PLAN_MOCK_STEPS: PlanStep[] = [
  {
    step_id: "step_1",
    text: "Extract property metadata for the target parcels",
    temporal_constraint: "current_turn",
    confidence: 0.82,
    order_hint: ["1"],
    tasks: [
      {
        task_id: "step_1_task_1",
        step_id: "step_1",
        intent: "Extract roof attributes (area, year built, stories) from the cadastral parcels",
        depends_on: [],
        candidates: [
          {
            capability_id: "property_metadata_extract",
            reason: "Covers the parcel-attribute extraction intent over the workarea batch",
            confidence: 0.91,
            resolvers: [{ resolver_id: "ortho_resolver", reason: "Highest-confidence resolver for parcel metadata", confidence: 0.88 }],
          },
        ],
        origin: "semantic_decomposition",
        superseded_by: null,
      },
      {
        task_id: "step_1_task_2",
        step_id: "step_1",
        intent: "Fetch the current ortho imagery collection covering the workarea",
        depends_on: ["step_1_task_1"],
        candidates: [
          {
            capability_id: "ortho_imagery_collections",
            reason: "Lists and validates the ortho collections intersecting the AOI",
            confidence: 0.86,
            resolvers: [{ resolver_id: "ortho_resolver", reason: "Imagery-collection resolver", confidence: 0.83 }],
          },
        ],
        origin: "semantic_decomposition",
        superseded_by: null,
      },
    ],
  },
  {
    step_id: "step_2",
    text: "Assess roof damage and produce the evidence report",
    temporal_constraint: "current_turn",
    confidence: 0.64,
    order_hint: ["2"],
    tasks: [
      {
        task_id: "step_2_task_1",
        step_id: "step_2",
        intent: "Score structural damage per parcel from the extracted attributes and ortho evidence",
        depends_on: ["step_1_task_1", "step_1_task_2"],
        candidates: [
          {
            capability_id: "damage_assessment",
            reason: "Produces the per-parcel damage scores the report consumes",
            confidence: 0.67,
            resolvers: [{ resolver_id: "ortho_resolver", reason: "Damage-scoring resolver", confidence: 0.64 }],
          },
        ],
        origin: "semantic_decomposition",
        superseded_by: null,
      },
      {
        task_id: "step_2_task_2",
        step_id: "step_2",
        intent: "Render the damage assessment report and evidence bundle",
        depends_on: ["step_2_task_1"],
        candidates: [
          {
            capability_id: "bundle_generate",
            reason: "Packages scores and evidence into the deliverable bundle",
            confidence: 0.7,
            resolvers: [{ resolver_id: "report_resolver", reason: "Report-bundle resolver", confidence: 0.75 }],
          },
        ],
        origin: "semantic_decomposition",
        superseded_by: null,
      },
    ],
  },
];

const PLAN_MOCK: PlanData = {
  plan_id: "plan-mock-1",
  plan_ref: "plan_ref-mock-1",
  revision: 2,
  plan_type: "normal",
  parent_plan_id: null,
  request_objective:
    "Assess roof damage for the downtown Miami parcels in the workarea: extract per-parcel roof attributes from cadastral and ortho evidence, score structural damage and deliver a report bundle with the damaged-parcel map layer.",
  display_name: "Miami roof damage assessment",
  steps: PLAN_MOCK_STEPS,
  phases: {
    semantic_decomposition: { status: "completed", failure: null, contract: null },
    capability_resolution: { status: "completed", failure: null, contract: null },
    provider_binding: { status: "completed", failure: null, contract: null },
    plan_assessment: { status: "completed", failure: null, estimation_plan: [], facts: [], profile: null, review_outcome: null },
    run_engine: {
      status: "in_progress",
      failure: null,
      task_executions: [
        {
          task_id: "step_1_task_1",
          state: "DONE",
          attempts: [
            {
              attempt_id: "attempt-step_1_task_1-1",
              task_id: "step_1_task_1",
              capability_id: "property_metadata_extract",
              resolver_id: "ortho_resolver",
              retry_count: 0,
              input_bindings: [{ role: "parcels_layer", evidence_id: "ev-catastro-parcels", confidence: 0.93 }],
              produced_evidence_id: "ev-property-metadata",
              own_contract: null,
            },
          ],
          satisfied_by: "ev-property-metadata",
          skipped_candidates: [],
          forced_bindings: null,
          diagnostics: {
            action_retry_count: 0,
            turn_retry_charge: 0,
            hitl_reask_count: 0,
            last_issue_code: null,
            last_decision: null,
            pending_reason: null,
          },
        },
        {
          task_id: "step_1_task_2",
          state: "READY",
          attempts: [],
          satisfied_by: null,
          skipped_candidates: [],
          forced_bindings: null,
          diagnostics: {
            action_retry_count: 0,
            turn_retry_charge: 0,
            hitl_reask_count: 0,
            last_issue_code: null,
            last_decision: null,
            pending_reason: null,
          },
        },
        {
          task_id: "step_2_task_1",
          state: "WAITING_INPUT",
          attempts: [
            {
              attempt_id: "attempt-step_2_task_1-1",
              task_id: "step_2_task_1",
              capability_id: "damage_assessment",
              resolver_id: "ortho_resolver",
              retry_count: 0,
              input_bindings: [{ role: "ortho_input", evidence_id: "ev-ortho-2024", confidence: 0.72 }],
              produced_evidence_id: null,
              own_contract: null,
            },
          ],
          satisfied_by: null,
          skipped_candidates: [],
          forced_bindings: null,
          diagnostics: {
            action_retry_count: 1,
            turn_retry_charge: 0,
            hitl_reask_count: 1,
            last_issue_code: "MISSING_INPUT",
            last_decision: "ASK_USER",
            pending_reason: {
              kind: "missing_input",
              resolver_id: "ortho_resolver",
              evidence_count_snapshot: 1,
              interrupt_payload: null,
              required_kinds: ["geotiff"],
            },
          },
        },
      ],
      run_config: null,
      execution: null,
    },
  },
  requested_change: null,
  state: "approved",
  transition_reason: "approved_by_user",
  failure: null,
  feedback_request: null,
  feedback_rounds: 0,
  turn_index: 3,
  history: [
    {
      revision: 1,
      request_objective:
        "Assess roof damage for the downtown Miami parcels in the workarea: extract per-parcel roof attributes from cadastral and ortho evidence, score structural damage and deliver a report bundle with the damaged-parcel map layer.",
      display_name: "Miami roof damage assessment",
      requested_change: null,
      turn_index: 2,
      steps: PLAN_MOCK_STEPS,
      accepted_decision_ids: [],
      execution: { outcome: "all_produced" },
      failure: null,
      closed_state: "executed",
      closed_reason: "revision superseded by a user-requested plan change",
    },
  ],
};

const PLAN_MOCKS: PlanMockItem[] = [
  {
    id: "plan_card_mid_run",
    label: "Plan card — approved plan mid-run (2 steps, engine in progress)",
    enabled: signal(false),
    view: "card",
    plan: PLAN_MOCK,
  },
  {
    id: "plan_progress_durable",
    label: "Plan progress — durable run-engine fallback (task executions)",
    enabled: signal(false),
    view: "progress",
    plan: PLAN_MOCK,
  },
];

// ── Evidence progress mock data (evidence_progress Custom events replayed into EvidenceProgressStore) ──

/** Wraps one bare `evidence_progress` payload in the AG-UI Custom envelope the store accepts. */
const evidenceEvent = (value: Record<string, unknown>): Record<string, unknown> => ({
  type: "Custom",
  name: "evidence_progress",
  value,
});

const EVIDENCE_PROGRESS_EVENTS: Record<string, unknown>[] = [
  // Episode 1 — task step_1_task_1: execute → api_call (ortho collection) → staging → execute/completed
  evidenceEvent({
    stage: "execute",
    status: "running",
    tool_id: "property_metadata_extract",
    task_id: "step_1_task_1",
    resolver_id: "ortho_resolver",
    label: "Property metadata extract",
  }),
  evidenceEvent({ stage: "api_call", status: "running", task_id: "step_1_task_1", layer: "ortho_imagery_collections" }),
  evidenceEvent({
    stage: "api_call",
    status: "completed",
    task_id: "step_1_task_1",
    size_bytes: 184_320,
    duration_ms: 2_400,
    layer: "ortho_imagery_collections",
  }),
  evidenceEvent({ stage: "staging", status: "running", task_id: "step_1_task_1" }),
  evidenceEvent({ stage: "staging", status: "completed", task_id: "step_1_task_1", size_bytes: 96_500, duration_ms: 800 }),
  evidenceEvent({
    stage: "execute",
    status: "completed",
    task_id: "step_1_task_1",
    size_bytes: 44_800,
    duration_ms: 3_600,
    kind: "geojson",
    label: "Property metadata extract",
  }),
  // Episode 2 — combined multi-task execution (task_id null is deliberate per the store contract)
  evidenceEvent({ stage: "execute", status: "running", tool_id: "ortho_extract", task_id: null }),
  evidenceEvent({ stage: "api_call", status: "running", task_id: null, layer: "ortho_imagery_collections" }),
  evidenceEvent({
    stage: "api_call",
    status: "completed",
    task_id: null,
    size_bytes: 2_500_000,
    duration_ms: 4_100,
    layer: "ortho_imagery_collections",
  }),
  evidenceEvent({ stage: "execute", status: "completed", task_id: null, size_bytes: 2_500_000, duration_ms: 5_200, kind: "geojson" }),
];

const EVIDENCE_MOCKS: EvidenceMockItem[] = [
  {
    id: "evidence_progress_full",
    label: "Evidence progress — full episode (extract + combined ortho)",
    enabled: signal(false),
    events: EVIDENCE_PROGRESS_EVENTS,
  },
];

// ── Geo hint chip mock data (inline chips — NEVER ContentBlockCardComponent, the geolocation bug class) ──

const GEO_CHIP_MOCKS: ContentBlockMockItem[] = [
  {
    id: "geo_chip_geolocation",
    label: "Geo hint chip — geolocation (formatted address)",
    enabled: signal(false),
    kind: "geolocation",
    data: {
      format: "geojson",
      label: "geocode",
      ref: null,
      value: MOCK_GEOJSON,
      metadata: {
        formatted_address: "123 NW 7th Ave, Miami FL",
        bbox: [-80.2, 25.76, -80.18, 25.78],
        center: [-80.19, 25.77],
        zoom_hint: 18,
        crs: "EPSG:4326",
        feature_count: 1,
      },
      source: {},
    },
  },
  {
    id: "geo_chip_place",
    label: "Geo hint chip — place (feature count, no address)",
    enabled: signal(false),
    kind: "place",
    data: {
      format: "geojson",
      label: "Downtown Miami parcels",
      ref: null,
      value: MOCK_GEOJSON,
      metadata: {
        bbox: [-80.21, 25.75, -80.17, 25.79],
        center: [-80.19, 25.77],
        zoom_hint: 15,
        crs: "EPSG:4326",
        feature_count: 2,
      },
      source: {},
    },
  },
];

// ── Workarea hint chip mock data (plan-level workarea geometry block, `data.layer` contract) ──

const WORKAREA_CHIP_MOCKS: ContentBlockMockItem[] = [
  {
    id: "workarea_chip",
    label: "Workarea chip — plan workarea AOI",
    enabled: signal(false),
    kind: "geometry",
    data: {
      format: "geojson",
      label: "workarea",
      ref: {
        file_id: "file-workarea-mock-1",
        url: "mock://files/workarea_plan_ref_mock_1.geojson",
        mime_type: "application/geo+json",
        size_bytes: 2_400,
        filename: "workarea_plan_ref-mock-1.geojson",
        preview_url: null,
      },
      value: null,
      metadata: { bbox: [-80.22, 25.74, -80.16, 25.8], crs: "EPSG:4326", feature_count: 1, content_summary: "Plan-level workarea AOI polygon" },
      source: { plan_id: "plan_ref-mock-1" },
      layer: { type: "workarea", plan_ref: "plan_ref-mock-1", entity: "plan", label: "Workarea AOI" },
    },
  },
];

// ── Service ────────────────────────────────────────────────────────────────────

/** Holds the enabled state and data for all debug mock scenarios. */
@Injectable({ providedIn: "root" })
export class MockDataService {
  readonly toolItems = TOOL_MOCKS;

  readonly hitlItems = HITL_MOCKS;

  readonly bgJobItems = BG_JOB_MOCKS;

  readonly reasoningItems = REASONING_MOCKS;

  readonly mapLayerItems = MAP_LAYER_MOCKS;

  readonly toolCallItems = TOOL_CALL_MOCKS;

  readonly planItems = PLAN_MOCKS;

  readonly evidenceItems = EVIDENCE_MOCKS;

  readonly geoChipItems = GEO_CHIP_MOCKS;

  readonly workareaChipItems = WORKAREA_CHIP_MOCKS;

  readonly enabledToolMocks = computed(() => this.toolItems.filter((i) => i.enabled()));

  readonly enabledHitlMocks = computed(() => this.hitlItems.filter((i) => i.enabled()).map((i) => i.data));

  readonly enabledBgJobMocks = computed(() => this.bgJobItems.filter((i) => i.enabled()).map((i) => i.snapshot));

  readonly enabledReasoningMocks = computed(() => this.reasoningItems.filter((i) => i.enabled()).map((i) => i.data));

  readonly enabledMapLayerMocks = computed(() => this.mapLayerItems.filter((i) => i.enabled()));

  readonly enabledToolCallMocks = computed(() => this.toolCallItems.filter((i) => i.enabled()).map((i) => i.data));

  readonly enabledPlanMocks = computed(() => this.planItems.filter((i) => i.enabled()));

  readonly enabledEvidenceMocks = computed(() => this.evidenceItems.filter((i) => i.enabled()));

  readonly enabledGeoChipMocks = computed(() => this.geoChipItems.filter((i) => i.enabled()));

  readonly enabledWorkareaChipMocks = computed(() => this.workareaChipItems.filter((i) => i.enabled()));

  readonly enabledCount = computed(
    () =>
      this.toolItems.filter((i) => i.enabled()).length +
      this.hitlItems.filter((i) => i.enabled()).length +
      this.bgJobItems.filter((i) => i.enabled()).length +
      this.reasoningItems.filter((i) => i.enabled()).length +
      this.mapLayerItems.filter((i) => i.enabled()).length +
      this.toolCallItems.filter((i) => i.enabled()).length +
      this.planItems.filter((i) => i.enabled()).length +
      this.evidenceItems.filter((i) => i.enabled()).length +
      this.geoChipItems.filter((i) => i.enabled()).length +
      this.workareaChipItems.filter((i) => i.enabled()).length,
  );

  readonly hasEnabled = computed(() => this.enabledCount() > 0);
}
